/**
 * ASL Sign Language Gesture Classifier
 * Uses MediaPipe hand landmarks to classify ASL letters and gestures
 * Landmark indices: https://developers.google.com/mediapipe/solutions/vision/hand_landmarker
 */

// Landmark indices
const WRIST = 0;
const THUMB_CMC = 1, THUMB_MCP = 2, THUMB_IP = 3, THUMB_TIP = 4;
const INDEX_MCP = 5, INDEX_PIP = 6, INDEX_DIP = 7, INDEX_TIP = 8;
const MIDDLE_MCP = 9, MIDDLE_PIP = 10, MIDDLE_DIP = 11, MIDDLE_TIP = 12;
const RING_MCP = 13, RING_PIP = 14, RING_DIP = 15, RING_TIP = 16;
const PINKY_MCP = 17, PINKY_PIP = 18, PINKY_DIP = 19, PINKY_TIP = 20;

function dist(a, b) {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2 + ((a.z || 0) - (b.z || 0)) ** 2);
}

function isFingerExtended(lm, tip, pip, mcp) {
  return lm[tip].y < lm[pip].y && lm[pip].y < lm[mcp].y;
}

function isFingerCurled(lm, tip, mcp) {
  return lm[tip].y > lm[mcp].y;
}

function isThumbExtended(lm) {
  return lm[THUMB_TIP].x < lm[THUMB_IP].x;
}

function isThumbDown(lm) {
  return lm[THUMB_TIP].y > lm[THUMB_IP].y && lm[THUMB_TIP].y > lm[WRIST].y;
}

// Check if two points are close
function isClose(a, b, threshold = 0.07) {
  return dist(a, b) < threshold;
}

// Check if fingertips touch thumb
function fingertipTouchesThumb(lm, fingerTip) {
  return isClose(lm[fingerTip], lm[THUMB_TIP], 0.08);
}

/**
 * Main classifier function
 * @param {Array} landmarks - array of 21 {x, y, z} normalized landmarks
 * @returns {{ letter: string, description: string, confidence: number }}
 */
export function classifyHandGesture(landmarks) {
  if (!landmarks || landmarks.length < 21) return null;
  const lm = landmarks;

  const indexUp = isFingerExtended(lm, INDEX_TIP, INDEX_PIP, INDEX_MCP);
  const middleUp = isFingerExtended(lm, MIDDLE_TIP, MIDDLE_PIP, MIDDLE_MCP);
  const ringUp = isFingerExtended(lm, RING_TIP, RING_PIP, RING_MCP);
  const pinkyUp = isFingerExtended(lm, PINKY_TIP, PINKY_PIP, PINKY_MCP);
  const thumbUp = isThumbExtended(lm);

  const indexCurled = isFingerCurled(lm, INDEX_TIP, INDEX_MCP);
  const middleCurled = isFingerCurled(lm, MIDDLE_TIP, MIDDLE_MCP);
  const ringCurled = isFingerCurled(lm, RING_TIP, RING_MCP);
  const pinkyCurled = isFingerCurled(lm, PINKY_TIP, PINKY_MCP);

  const thumbTipY = lm[THUMB_TIP].y;
  const wristY = lm[WRIST].y;
  const palmHeight = Math.abs(lm[MIDDLE_MCP].y - wristY);

  // --- SPECIAL GESTURES ---

  // LOVE / ILY: index + pinky up, thumb out
  if (indexUp && pinkyUp && !middleUp && !ringUp) {
    return { letter: 'ILY ❤️', description: 'I Love You (ASL)', confidence: 0.9 };
  }

  // THUMBS UP
  if (!indexUp && !middleUp && !ringUp && !pinkyUp &&
      thumbTipY < lm[THUMB_IP].y && lm[THUMB_TIP].y < lm[INDEX_MCP].y) {
    return { letter: '👍', description: 'Thumbs Up — Good / Yes', confidence: 0.88 };
  }

  // THUMBS DOWN
  if (!indexUp && !middleUp && !ringUp && !pinkyUp && isThumbDown(lm)) {
    return { letter: '👎', description: 'Thumbs Down — Bad / No', confidence: 0.85 };
  }

  // OPEN HAND / STOP
  if (indexUp && middleUp && ringUp && pinkyUp && thumbUp) {
    return { letter: '✋', description: 'Open Hand — Hello / Stop', confidence: 0.92 };
  }

  // FIST / S
  if (indexCurled && middleCurled && ringCurled && pinkyCurled && !thumbUp) {
    return { letter: 'S ✊', description: 'Letter S / Fist — Power / Ready', confidence: 0.87 };
  }

  // PEACE / V
  if (indexUp && middleUp && !ringUp && !pinkyUp && !thumbUp) {
    const spread = Math.abs(lm[INDEX_TIP].x - lm[MIDDLE_TIP].x);
    if (spread > 0.04) {
      return { letter: 'V ✌️', description: 'Letter V / Peace', confidence: 0.9 };
    }
    return { letter: 'U', description: 'Letter U — Two fingers together', confidence: 0.82 };
  }

  // POINTING / INDEX ONLY
  if (indexUp && !middleUp && !ringUp && !pinkyUp) {
    return { letter: 'D / Pointing ☝️', description: 'Letter D / Pointing — You / One', confidence: 0.85 };
  }

  // CALL ME / Y / SHAKA
  if (!indexUp && !middleUp && !ringUp && pinkyUp && thumbUp) {
    return { letter: 'Y 🤙', description: 'Letter Y / Call Me — Hang Loose', confidence: 0.88 };
  }

  // L shape: thumb + index
  if (indexUp && thumbUp && !middleUp && !ringUp && !pinkyUp) {
    return { letter: 'L 👈', description: 'Letter L — L-shape with index and thumb', confidence: 0.87 };
  }

  // W: three fingers up
  if (indexUp && middleUp && ringUp && !pinkyUp) {
    return { letter: 'W', description: 'Letter W — Three fingers raised', confidence: 0.84 };
  }

  // ROCK ON: index + pinky + thumb
  if (indexUp && !middleUp && !ringUp && pinkyUp && thumbUp) {
    return { letter: '🤘 Rock On', description: 'Rock On / Metal Horns', confidence: 0.86 };
  }

  // OK: thumb + index circle
  if (isClose(lm[THUMB_TIP], lm[INDEX_TIP], 0.07) && middleUp && ringUp && pinkyUp) {
    return { letter: 'O / OK 👌', description: 'Letter O / OK — Perfect / Yes', confidence: 0.88 };
  }

  // C: all fingers curved (not fully extended, not curled)
  const allPartialCurl =
    lm[INDEX_TIP].y < lm[INDEX_MCP].y && lm[INDEX_TIP].y > lm[INDEX_PIP].y &&
    lm[MIDDLE_TIP].y < lm[MIDDLE_MCP].y &&
    lm[RING_TIP].y < lm[RING_MCP].y;
  if (allPartialCurl && !pinkyUp) {
    return { letter: 'C', description: 'Letter C — Curved hand like holding a ball', confidence: 0.76 };
  }

  // B: flat hand all up, thumb tucked
  if (indexUp && middleUp && ringUp && pinkyUp && !thumbUp) {
    return { letter: 'B', description: 'Letter B — Flat hand, fingers together, thumb tucked', confidence: 0.83 };
  }

  // A: fist with thumb resting on side
  if (indexCurled && middleCurled && ringCurled && pinkyCurled && thumbUp) {
    return { letter: 'A', description: 'Letter A — Fist with thumb resting on side', confidence: 0.82 };
  }

  // I: only pinky up
  if (!indexUp && !middleUp && !ringUp && pinkyUp && !thumbUp) {
    return { letter: 'I', description: 'Letter I — Pinky finger raised', confidence: 0.88 };
  }

  // F: index+thumb touch, others up
  if (isClose(lm[THUMB_TIP], lm[INDEX_TIP], 0.07) && middleUp && ringUp) {
    return { letter: 'F', description: 'Letter F — Index and thumb touching, others up', confidence: 0.8 };
  }

  // R: index+middle up and crossed
  if (indexUp && middleUp && !ringUp && !pinkyUp) {
    const crossed = lm[INDEX_TIP].x < lm[MIDDLE_TIP].x;
    if (crossed) return { letter: 'R', description: 'Letter R — Index and middle fingers crossed', confidence: 0.78 };
  }

  // K: index + middle up, thumb between
  if (indexUp && middleUp && !ringUp && !pinkyUp && thumbUp) {
    return { letter: 'K', description: 'Letter K — Index, middle up with thumb', confidence: 0.79 };
  }

  return { letter: '?', description: 'No recognized gesture — try a clear ASL sign', confidence: 0 };
}

export const ASL_DICTIONARY = {
  A: { emoji: '🤜', description: 'Fist with thumb resting on side', category: 'alphabet' },
  B: { emoji: '🖐', description: 'Flat hand, fingers together pointing up, thumb tucked', category: 'alphabet' },
  C: { emoji: '🤏', description: 'Curved hand like holding a ball', category: 'alphabet' },
  D: { emoji: '☝️', description: 'Index finger up, other fingers curved touching thumb', category: 'alphabet' },
  F: { emoji: '🤌', description: 'Index finger and thumb touching, other fingers up', category: 'alphabet' },
  I: { emoji: '🤙', description: 'Pinky finger raised, others curled', category: 'alphabet' },
  K: { emoji: '✌️', description: 'Index and middle fingers up, thumb between them', category: 'alphabet' },
  L: { emoji: '👈', description: 'L-shape: index finger up, thumb out', category: 'alphabet' },
  O: { emoji: '👌', description: 'All fingers curved to touch thumb forming O', category: 'alphabet' },
  R: { emoji: '🤞', description: 'Index and middle fingers crossed', category: 'alphabet' },
  S: { emoji: '✊', description: 'Fist with thumb over fingers', category: 'alphabet' },
  U: { emoji: '✌️', description: 'Index and middle fingers up together', category: 'alphabet' },
  V: { emoji: '✌️', description: 'Index and middle fingers up in V shape', category: 'alphabet' },
  W: { emoji: '🖖', description: 'Three fingers up (index, middle, ring)', category: 'alphabet' },
  Y: { emoji: '🤙', description: 'Thumb and pinky out — hang loose', category: 'alphabet' },
  '👍': { emoji: '👍', description: 'Thumb pointing up', category: 'expression' },
  '👎': { emoji: '👎', description: 'Thumb pointing down', category: 'expression' },
  '✋': { emoji: '✋', description: 'Open hand — Hello or Stop', category: 'expression' },
  '❤️': { emoji: '🤟', description: 'I Love You in ASL', category: 'expression' },
  '🤘': { emoji: '🤘', description: 'Rock On / Metal Horns', category: 'expression' },
};
